import {
  selectTechnique,
  selectTechniqueAdvice,
} from "domain/techniques/techniques.selectors";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import {
  TechniqueContext,
  TechniqueType,
} from "domain/entities/techniques.entities";
import { Trans } from "react-i18next";
import {
  HiArrowLeft,
  HiDotsVertical,
  HiHeart,
  HiOutlineHeart,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { translate } from "ui/i18n";
import { TechniqueBanner } from "ui/pages/app/techniques/technique/TechniqueBanner";
import { getDurationInHours } from "ui/utils/formatters";
import { SequenceComponent } from "ui/pages/app/techniques/technique/SequenceComponent";
import { Opinions } from "ui/pages/app/components/activities/Opinions";
import { SecondaryButton } from "ui/pages/app/components/buttons/SecondaryButton";
import { useWindowWidth } from "ui/utils/hooks";
import { Advice } from "ui/pages/app/components/activities/Advice";
import { Materiels } from "ui/pages/app/components/activities/Materiels";
import { useDisclosure } from "@chakra-ui/hooks";
import { selectFavoriteTechniques } from "domain/activities/activities.selectors";
import { toggleActivityFavorite } from "ui/utils/toggle-activity-favorite";
import { selectIsPremium } from "domain/auth/auth.selectors";
import { Snackbar } from "ui/pages/snackbar";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { TechniquePDF } from "ui/pages/app/techniques/technique/TechniquePDF";
import { useEffect, useState } from "react";
import {
  getTechnique,
  getTechniqueAdvice,
} from "domain/techniques/techniques.actions";
import { Popover, PopoverContent } from "@chakra-ui/react";
import { DownloadImageIcon } from "ui/assets/svg/icons/DownloadImageIcon";
import { DownloadActivityTooltip } from "ui/pages/app/components/activities/DownloadActivityTooltip";
import { DownloadActivityImagesTooltip } from "ui/pages/app/components/activities/techniques/DownloadActivityImagesTooltip";
import { selectOnBoardingValues } from "domain/on-boarding/on-boarding.selectors";
import { i18nKey } from "domain/entities/activities.entities";
import { PDFIcon } from "ui/assets/svg/icons/PDFIcon";
import { getDownloadLink } from "domain/activities/activities.actions";

export function Technique({
  selectedTechniqueId,
}: {
  selectedTechniqueId: string | null;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const seeMoreOpinions = useDisclosure();
  const popoverState = useDisclosure();
  const advice = useAppSelector(selectTechniqueAdvice);
  const isPremium = useAppSelector(selectIsPremium);
  const technique = useAppSelector(selectTechnique);
  const onBoardingValues = useAppSelector(selectOnBoardingValues);
  const favoriteTechnique = useAppSelector(selectFavoriteTechniques).find(
    (t) => t.techniqueId === technique?.id
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (selectedTechniqueId) {
      fetchInfos();
    }
  }, [selectedTechniqueId]); //eslint-disable-line

  const fetchInfos = async () => {
    const { success, technique } = await dispatch(
      getTechnique({ id: Number(selectedTechniqueId) })
    ).unwrap();
    if (success) {
      if (technique?.premium && !isPremium) {
        navigate("/techniques");
      }
      await dispatch(getTechniqueAdvice(Number(selectedTechniqueId)));
    } else navigate("/techniques");
    setIsLoading(false);
  };

  const handleFavoriteClick = async (e: any) => {
    e.stopPropagation();
    toggleActivityFavorite({
      favoriteActivity: favoriteTechnique,
      dispatch,
      activityId: technique.id,
      activityType: "technique",
    });
  };

  const handleDownloadClick = async (e: any) => {
    e.stopPropagation();
    if (!isPremium) {
      await Snackbar.removeDownloadActivityAlert();
      Snackbar.showDownloadActivityAlert(() =>
        navigate("/account", { replace: true })
      );
    }
  };

  const handleDownloadPDFClick = (e: any) => {
    e.stopPropagation();
    Snackbar.showSuccess({
      title: translate("ns1:Technique.DownloadPDFSuccess"),
      variant: "secondary",
    });
  };

  const handleDownloadImagesClick = async (e: any) => {
    e.stopPropagation();
    const result = await dispatch(
      getDownloadLink({
        type: "whiteboard",
        id: `techniques/${technique.innizRef}.zip`,
      })
    ).unwrap();
    if (result.success && result.url) {
      window.open(result.url, "_blank");
      Snackbar.showSuccess({
        title: translate("ns1:Technique.DownloadVisualsSuccess"),
        variant: "secondary",
      });
    }
  };

  return (
    <div className="flex h-full flex-col bg-darkGrey-100 p-4 md:flex-row md:py-8 md:pl-6 md:pr-10">
      <div className="mb-4 flex w-full items-center justify-between md:my-0 md:w-12 md:items-start">
        <button
          onClick={() => navigate(-1)}
          className="z-50 flex h-5 items-center"
        >
          <HiArrowLeft size={24} className="text-darkGrey-800" />
        </button>
        {!isDesktop && (
          <div className="flex w-fit space-x-4">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={handleFavoriteClick}
            >
              {!!favoriteTechnique?.id ? (
                <HiHeart className="text-secondaryGrey-900" size={20} />
              ) : (
                <HiOutlineHeart className="text-secondaryGrey-900" size={20} />
              )}
            </button>
            <Popover
              isOpen={popoverState.isOpen}
              onOpen={popoverState.onOpen}
              onClose={popoverState.onClose}
              placement="right-start"
            >
              <DownloadActivityImagesTooltip activity="technique">
                {onBoardingValues.closedDownloadActivityImages ? (
                  <DownloadActivityTooltip type="technique" mobile={!isDesktop}>
                    <button
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        popoverState.isOpen ? "bg-darkGrey-200" : "bg-white"
                      }`}
                    >
                      <HiDotsVertical
                        className={`${
                          popoverState.isOpen
                            ? "text-darkGrey-700"
                            : "text-secondaryGrey-900"
                        }`}
                        size={20}
                      />
                    </button>
                  </DownloadActivityTooltip>
                ) : (
                  <button
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                      popoverState.isOpen ? "bg-darkGrey-200" : "bg-white"
                    }`}
                  >
                    <HiDotsVertical
                      className={`${
                        popoverState.isOpen
                          ? "text-darkGrey-700"
                          : "text-secondaryGrey-900"
                      }`}
                      size={20}
                    />
                  </button>
                )}
              </DownloadActivityImagesTooltip>
              {popoverState.isOpen && (
                <PopoverContent
                  style={{
                    isolation: "isolate",
                    zIndex: 100,
                    width: "fit-content",
                    border: "none",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                    {technique?.consignesCount > 0 && (
                      <button
                        className={`flex w-full rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                        onClick={handleDownloadImagesClick}
                      >
                        <span className="me-2">
                          <DownloadImageIcon color="#2b3674" />
                        </span>
                        {translate("ns1:Technique.DownloadImages")}
                      </button>
                    )}
                    {isPremium && technique ? (
                      <PDFDownloadLink
                        document={
                          <TechniquePDF technique={technique} advice={advice} />
                        }
                        fileName={`${technique.title}.pdf`}
                        className="w-full"
                      >
                        <button
                          className={`flex w-full rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                          onClick={handleDownloadPDFClick}
                        >
                          <PDFIcon
                            className="me-2 text-secondaryGrey-900"
                            size={20}
                          />
                          {translate("ns1:Button.Download")}
                        </button>
                      </PDFDownloadLink>
                    ) : (
                      <button
                        className={`flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                        onClick={handleDownloadClick}
                      >
                        <PDFIcon
                          className="me-2 text-secondaryGrey-900"
                          size={20}
                        />
                        {translate("ns1:Button.Download")}
                      </button>
                    )}
                  </div>
                </PopoverContent>
              )}
            </Popover>
          </div>
        )}
      </div>

      {technique && !isLoading ? (
        <>
          <div className="flex w-full flex-col space-y-6 md:space-y-8">
            <TechniqueBanner
              technique={technique}
              isFavorite={!!favoriteTechnique?.id}
              handleFavoriteClick={handleFavoriteClick}
              handleDownloadClick={handleDownloadClick}
              handleDownloadPDFClick={handleDownloadPDFClick}
              handleDownloadImagesClick={handleDownloadImagesClick}
            />

            <div className="flex flex-wrap gap-3 md:justify-start">
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-700 px-3 text-xs font-semibold text-white">
                {translate(`ns1:Techniques.${technique.type as TechniqueType}`)}
              </div>
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                {translate(
                  `ns1:Techniques.${technique.context as TechniqueContext}`
                )}
              </div>
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                {getDurationInHours(Number(technique.duration))}
              </div>
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                {technique.onsite &&
                  technique.remote &&
                  translate("ns1:Activities.remote&onsite")}
                {technique.onsite &&
                  !technique.remote &&
                  translate("ns1:Activities.onsite")}
                {!technique.onsite &&
                  technique.remote &&
                  translate("ns1:Activities.remote")}
              </div>
              {technique.participants ? (
                <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                  <Trans
                    i18nKey={"Activities.UntilParticipants" as i18nKey}
                    values={{ participants: technique.participants }}
                  />
                </div>
              ) : (
                <></>
              )}
              {technique.participants === 0 && (
                <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                  <Trans
                    i18nKey={"Activities.MoreThanParticipants" as i18nKey}
                    values={{ participants: 20 }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col space-x-0 space-y-6 md:flex-row md:space-x-8 md:space-y-0">
              <div className="flex w-full flex-col space-y-6 md:space-y-8">
                <div className="flex flex-col space-y-2 rounded-[30px] bg-white p-4 py-6 md:p-6 md:pb-2">
                  <h4 className="text-base font-bold md:text-lg">
                    {translate("ns1:Activities.Goal")}
                  </h4>
                  <span className="text-sm font-medium md:text-base">
                    {technique.objectif}
                  </span>
                  {technique.illustration && (
                    <div className="!mt-4 flex justify-center md:!mt-6">
                      <img
                        src={`${
                          technique.illustration
                        }?timestamp=${Date.now()}`}
                        alt="technique illustration"
                        className="w-full max-w-[370px]"
                      />
                    </div>
                  )}
                </div>

                <div className="rounded-[30px] bg-white p-4 py-6 md:p-6">
                  {technique.sequences.length > 0 &&
                    technique.sequences.map((sequence, index) => (
                      <SequenceComponent
                        key={"sequence" + index}
                        sequence={sequence}
                        index={index}
                        sequencesLength={technique.sequences.length}
                      />
                    ))}
                </div>

                {/* advice and equipment for mobile */}
                {!isDesktop && (advice || technique.materiels.length > 0) && (
                  <div className="flex flex-col space-y-6 md:space-y-8">
                    {technique.materiels.length > 0 &&
                      technique.materiels[0].id && (
                        <Materiels
                          materiels={technique.materiels}
                          visuels={technique.visuels}
                        />
                      )}
                    {advice && <Advice advice={advice} />}
                  </div>
                )}

                {technique.level !== "CUSTOM" && (
                  <div className="rounded-[30px] bg-white p-4 py-6 md:p-6">
                    <div className="flex flex-col items-center justify-center space-y-1.5">
                      {technique.testimonials?.length > 0 ? (
                        <>
                          <p className="text-xl font-bold text-secondaryGrey-900">
                            <Trans
                              i18nKey={"Technique.NumberOfReviews" as i18nKey}
                              values={{
                                reviews: technique.testimonials.length,
                              }}
                            />
                          </p>
                          <p className="text-sm font-medium text-secondaryGrey-800">
                            {translate("ns1:Technique.ForThisTechnique")}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-3xl font-bold text-secondaryGrey-900">
                            {translate("ns1:Technique.NoReviewsYet")}
                          </p>
                          <p className="text-base font-medium text-secondaryGrey-800">
                            {translate("ns1:Technique.ForThisTechnique")}
                          </p>
                        </>
                      )}
                    </div>

                    <Opinions
                      testimonials={technique.testimonials.slice(
                        0,
                        seeMoreOpinions.isOpen
                          ? technique.testimonials.length
                          : 3
                      )}
                    />

                    {technique.testimonials.length > 3 && (
                      <div className="mt-16 flex flex-row justify-center space-x-6">
                        {/* <PrimaryButton height="36px">
                      {translate("ns1:Activities.Buttons.LeaveAReview")}
                    </PrimaryButton> */}
                        <SecondaryButton
                          height="36px"
                          onClick={seeMoreOpinions.onToggle}
                        >
                          {translate(
                            `ns1:Activities.Buttons.${
                              seeMoreOpinions.isOpen
                                ? "SeeLessReviews"
                                : "SeeMoreReviews"
                            }`
                          )}
                        </SecondaryButton>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* advice and equipment for desktop */}
              {isDesktop && (advice || technique.materiels.length > 0) && (
                <div className="flex w-80 flex-col space-y-6 md:space-y-8">
                  {technique.materiels.length > 0 &&
                    technique.materiels[0].id && (
                      <Materiels
                        materiels={technique.materiels}
                        visuels={technique.visuels}
                      />
                    )}
                  {advice && <Advice advice={advice} />}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <div className="loader" />
        </div>
      )}
    </div>
  );
}
